// Refill add-on shown under the buy buttons (markup: snippets/product-variant-options.liquid,
// picker_type 'refill_upsell'). Adds the chosen refill variant to the cart via AJAX and
// hands the response to the cart drawer / notification, like product-form.js does.
if (!customElements.get('refill-upsell')) {
  customElements.define(
    'refill-upsell',
    class RefillUpsell extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.refill-upsell__button');
        this.buttonText = this.querySelector('.refill-upsell__button-text');
        this.thumb = this.querySelector('.refill-upsell__thumb');
        this.amount = this.querySelector('.refill-upsell__amount');
        this.compare = this.querySelector('.refill-upsell__compare');
        this.errorMessage = this.querySelector('.refill-upsell__error');
        this.supply = this.querySelector('.refill-upsell__supply');
        this.thumbButton = this.querySelector('.refill-upsell__thumb-button');
        this.zoom = this.querySelector('.refill-upsell__zoom');

        this.addEventListener('change', (event) => {
          if (event.target.matches('.refill-upsell__input')) this.showOption(event.target);
        });
        this.button.addEventListener('click', this.onAdd.bind(this));

        if (this.thumbButton && this.zoom && typeof this.zoom.showModal === 'function') {
          this.thumbButton.addEventListener('click', this.openZoom.bind(this));
          this.zoom.querySelector('.refill-upsell__zoom-close').addEventListener('click', () => this.zoom.close());
          // The dialog fills the screen as the dimmed layer, so a tap on it (not the panel) is outside
          this.zoom.addEventListener('click', (event) => {
            if (event.target === this.zoom) this.zoom.close();
          });
          this.zoom.addEventListener('close', () => {
            document.body.classList.remove('overflow-hidden');
            this.thumbButton.focus();
          });
        } else if (this.thumbButton) {
          // No <dialog> support: leave the thumbnail as a plain image
          this.thumbButton.disabled = true;
        }

        if (!this.selectedInput) {
          const firstAvailable = this.querySelector('.refill-upsell__input:not(:disabled)');
          if (firstAvailable) firstAvailable.checked = true;
        }
        if (this.selectedInput) this.showOption(this.selectedInput);

        if (this.hasAttribute('data-landed-refill')) {
          this.removeAttribute('data-landed-refill');
          this.handleLandedRefill();
        }
      }

      // The page opened on a refill variant (?variant= link). main-product.liquid already rendered
      // the page on the first starter set and the refill is preselected here, so point the URL at
      // that starter set and bring this module into view.
      handleLandedRefill() {
        const selectedVariant = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        try {
          const variantId = JSON.parse(selectedVariant?.textContent || 'null')?.id;
          if (variantId) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variantId);
            window.history.replaceState({}, '', url.toString());
          }
        } catch (error) {
          console.error(error);
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        this.classList.add('refill-upsell--highlight');
        setTimeout(() => this.classList.remove('refill-upsell--highlight'), 2400);
      }

      get selectedInput() {
        return this.querySelector('.refill-upsell__input:checked');
      }

      showOption(input) {
        const { thumb, price, compare } = input.dataset;
        if (this.thumb && thumb) this.thumb.src = thumb;
        this.amount.textContent = price;
        this.compare.textContent = compare || '';
        this.compare.hidden = !compare;
        this.showSupply(input.id);
        this.setError(false);
        this.setState('idle');
      }

      openZoom() {
        const input = this.selectedInput;
        if (!input) return;

        const image = this.zoom.querySelector('.refill-upsell__zoom-image');
        const caption = this.zoom.querySelector('.refill-upsell__zoom-caption');
        const label = input.labels?.[0]?.textContent.trim() || '';

        image.src = input.dataset.large || input.dataset.thumb;
        image.alt = label;
        caption.textContent = [label, input.dataset.price].filter(Boolean).join(' · ');

        // overflow-hidden locks page scroll and hides the sticky add-to-cart bar (sticky-atc.liquid)
        document.body.classList.add('overflow-hidden');
        this.zoom.showModal();
      }

      showSupply(inputId) {
        if (!this.supply) return;
        let hasLine = false;
        this.supply.querySelectorAll('.refill-upsell__supply-line').forEach((line) => {
          const isCurrent = line.dataset.for === inputId;
          line.hidden = !isCurrent;
          if (isCurrent) hasLine = true;
        });
        this.supply.hidden = !hasLine;
      }

      setState(state) {
        clearTimeout(this.resetTimer);
        this.button.classList.toggle('loading', state === 'loading');
        this.button.classList.toggle('is-added', state === 'added');
        this.button.setAttribute('aria-disabled', state === 'loading');
        this.buttonText.textContent = this.button.dataset[`label${state[0].toUpperCase()}${state.slice(1)}`];
        if (state === 'added') this.resetTimer = setTimeout(() => this.setState('idle'), 2500);
      }

      setError(message) {
        this.errorMessage.hidden = !message;
        this.errorMessage.textContent = message || '';
      }

      onAdd() {
        const input = this.selectedInput;
        if (!input || this.button.getAttribute('aria-disabled') === 'true') return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { id: Number(input.value), quantity: 1 };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(this.button);
        }

        this.setError(false);
        this.setState('loading');

        fetch(routes.cart_add_url, { ...fetchConfig('javascript'), body: JSON.stringify(body) })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'refill-upsell',
                productVariantId: body.id,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.setError(response.description || response.message || window.cartStrings.error);
              this.setState('idle');
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'refill-upsell',
              productVariantId: body.id,
              cartData: response,
            });
            this.setState('added');

            if (cart) {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(response);
            }
          })
          .catch((error) => {
            console.error(error);
            this.setError(window.cartStrings.error);
            this.setState('idle');
          });
      }
    }
  );
}
